<template>
  <!-- Tablet and up -->
  <v-container v-if="smAndUp" class="">
    <v-row :justify="route.name === 'Home' ? 'start' : 'center'">
      <v-sheet
        color="white"
        width="100%"
        height="44px"
        rounded="xl"
        class="d-flex align-center justify-center px-1"
        :style="{ maxWidth: route.name === 'Home' ? '900px' : '100%' }"
      >
        <!-- px-2 mb-lg-10 -->
        <ServicesField @keydown.enter="search" />
        <v-divider thickness="2" vertical inset class="mx-2"></v-divider>
        <LocationField @keydown.enter="search" ref="theLocationField" />
        <v-btn
          class="px-2 d-flex justify-center align-center"
          color="#1A73E9"
          append-icon="mdi-chevron-right"
          :icon="!mdAndUp"
          :size="!mdAndUp ? 38 : 'default'"
          :loading="shouldShowLoading"
          @click="search"
        >
          <template v-if="mdAndUp">
            <span>{{ $t('searchPage.searchButton') }}</span>
          </template>
          <v-icon v-if="!mdAndUp" icon="mdi-chevron-right"></v-icon>
        </v-btn>
      </v-sheet>
    </v-row>
  </v-container>
  <!-- Mobile part -->
  <div class="d-flex flex-column align-stretch w-100 ga-4 mb-8" v-if="!smAndUp">
    <ServicesField />
    <LocationField ref="theLocationField" />

    <v-btn
      style="height: 48px; font-size: 18px; letter-spacing: normal"
      color="blue-darken-1"
      class="mt-1"
      append-icon="mdi-chevron-right"
      :loading="shouldShowLoading"
      @click="search"
    >
      {{ $t('searchPage.searchButton') }}
    </v-btn>
  </div>
</template>

<script lang="ts" setup>
import { useDisplay } from 'vuetify'
import { useMainSearchBarStore } from '@/store/search-page/mainSearchBarStore'
import ServicesField from '@/components/MainSearchBar/ServicesField.vue'
import LocationField from '@/components/MainSearchBar/LocationField.vue'
import { useRoute } from 'vue-router'
import { computed, onMounted, watch, ref, nextTick } from 'vue'
import { useAutocompleteLocationStore } from '@/store/address-autocomplete/autocompleteAddressStore'
import { useGeolocationStore } from '@/store/geolocation/geolocationStore'
import { IStoredGeolocation } from '@/store/geolocation/geolocation'
import router from '@/router'
import { getDisciplineSlug } from '@/utils/discipline'
import { useGoogleMapsStore } from '@/store/google-maps/googleMapsStore'
import { ApiResponse } from '@/api/api'
import { ISearchAddress } from '@/store/search-page/mainSearchBarStore.d'
const route = useRoute()
const { smAndUp, mdAndUp } = useDisplay()
const mainSearchBarStore = useMainSearchBarStore()
const geolocationStore = useGeolocationStore()
const autocompleteLocationStore = useAutocompleteLocationStore()
const googleMapsStore = useGoogleMapsStore()
const theLocationField = ref(null)

const formatCityName = (city: string) => {
  if (!city) return

  return city
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

watch(
  () => route.fullPath,
  async (_, __) => {
    mainSearchBarStore.isInitializing = true
    mainSearchBarStore.listPractitioners = []
    // get discipline from route
    const disciplineFromRoute = (route.params.disciplineSlug ?? '') as string
    // get city from route
    const cityFromRoute = (route.params.city ?? '') as string
    // get precise address from route
    const lon = (route.query.lon ?? '') as string
    const lat = (route.query.lat ?? '') as string
    const address = (route.query.address ?? '') as string
    // set precise location in case we have it from route

    if (cityFromRoute) {
      if (cityFromRoute && disciplineFromRoute && !(lon || lat)) {
        const { status, data: res }: any =
          await mainSearchBarStore.getAddrByCityAndDiscipline(
            cityFromRoute,
            disciplineFromRoute,
          )

        if (status === 200) {
          geolocationStore.updateStoredLocation({
            // update to localStorage
            city: res.city,
            addressLine: cityFromRoute,
            country: res.countrySlug,
            coords: {
              latitude: Number(res.latitude),
              longitude: Number(res.longitude),
            },
          })
          mainSearchBarStore.searchLocation = res.city
        }
      } else {
        await autocompleteLocationStore.autocompleteRequest(route.params.city)

        const locationItem = await googleMapsStore.getFormatedAddressFromPlace(
          autocompleteLocationStore.autocompleteLocationList[0].value,
        )

        autocompleteLocationStore.selectedLocation = {
          ...autocompleteLocationStore.selectedLocation,
          label: locationItem.addressLine,
          value: locationItem,
        }

        geolocationStore.updateStoredLocation({
          // update to localStorage
          ...locationItem,
          coords: {
            latitude: Number(locationItem.latitude),
            longitude: Number(locationItem.longitude),
          },
        })

        if (lon && lat) {
          geolocationStore.storedLocation = {
            ...geolocationStore.storedLocation,
            coords: {
              latitude: locationItem.latitude,
              longitude: locationItem.longitude,
            },
          }
        }
      }

      // search()
    } else {
      if (lon && lat) {
        geolocationStore.storedLocation = {
          ...geolocationStore.storedLocation,
          coords: { latitude: lat, longitude: lon },
        }
      }
    }

    if (address) {
      geolocationStore.storedLocation = {
        ...geolocationStore.storedLocation,
        address,
      }
    }

    if (!mainSearchBarStore.searchService)
      mainSearchBarStore.searchService = disciplineFromRoute
    if (!mainSearchBarStore.searchLocation)
      mainSearchBarStore.searchLocation = cityFromRoute || null

    // do search
    await mainSearchBarStore.findYourPractitioner()
    mainSearchBarStore.isInitializing = false
    theLocationField.value.renderingLocationField()
  },
  { immediate: true },
)

// watch(() => route.fullPath,
//   async () => {
//     const geolocationStore = useGeolocationStore()
//     const autocompleteLocationStore = useAutocompleteLocationStore()
//     const googleMapsStore = useGoogleMapsStore()

//     let query: any = {
//       lat: locationItem.latitude,
//       lon: locationItem.longitude,
//     }

//   }
// )

const storeLat = computed(() => {
  return geolocationStore.getLat()
})

const storeLon = computed(() => {
  return geolocationStore.getLon()
})

const selectedLat = computed(() => {
  return parseFloat(autocompleteLocationStore.selectedLocation.value.latitude)
})

const selectedLon = computed(() => {
  return parseFloat(autocompleteLocationStore.selectedLocation.value.longitude)
})

const search = async () => {
  let lat = storeLat.value
  let lon = storeLon.value

  // get discipline slug from field
  const serviceOriginal = mainSearchBarStore.searchService

  const disciplineSlug = await getDisciplineSlug(serviceOriginal)

  if (
    !['SearchPage', 'DisciplineSearchPage'].includes(
      router.currentRoute.value.name as string,
    )
  ) {
    if (
      !mainSearchBarStore.searchLocation &&
      !mainSearchBarStore.searchService
    ) {
      await router.push({ name: 'SearchPage' })
      return
    }

    if (
      mainSearchBarStore.searchLocation &&
      !mainSearchBarStore.searchService
    ) {
      let query: any = {
        lat: geolocationStore.getLat(),
        lon: geolocationStore.getLon(),
      }
      if (!geolocationStore.storedLocationIsEmpty) {
        query.address = (geolocationStore.storedLocation as any).city
      }
      await router.push({
        name: 'SearchPage',
        query,
      })
      return
    }

    if (
      !mainSearchBarStore.searchLocation &&
      mainSearchBarStore.searchService
    ) {
      await router.push({
        name: 'DisciplineSearchPage',
        params: { disciplineSlug: disciplineSlug },
      })
      return
    }
  }

  // get city from field
  let city = ''

  if (
    autocompleteLocationStore.selectedLocation &&
    autocompleteLocationStore.hasCityProp
  ) {
    // case user has selected an item in the menu of the location field
    city = autocompleteLocationStore.selectedLocation.value.city
    // update location in local storage
    lat = selectedLat.value
    lon = selectedLon.value

    geolocationStore.updateStoredLocation({
      city: autocompleteLocationStore.selectedLocation.value.city,
      coords: {
        latitude: lat,
        longitude: lon,
      },
    })
  }

  // case user has just put a text in city field
  if (mainSearchBarStore.searchLocation && !city) {
    // case user has selected an item in the menu of the location field
    city = mainSearchBarStore.searchLocation
  }

  try {
    let path = ''
    if (disciplineSlug && city) {
      path = `/${encodeURIComponent(disciplineSlug)}/${encodeURIComponent(city.toLowerCase())}`
    } else if (disciplineSlug && !mainSearchBarStore.searchLocation) {
      path = `/${encodeURIComponent(disciplineSlug)}`
    }

    const query: any = {}
    if (lat && mainSearchBarStore.searchLocation) query.lat = lat
    if (lon && mainSearchBarStore.searchLocation) query.lon = lon
    if (mainSearchBarStore.searchLocation)
      query.address = mainSearchBarStore.searchLocation

    await router.push({
      path,
      query,
    })
  } catch (err) {
    console.error('Error', err)
  }
}

const shouldShowLoading = computed(() => {
  return (
    ['SearchPage', 'DisciplineCitySearchPage', 'DisciplineSearchPage'].includes(
      route.name as string,
    ) && mainSearchBarStore.loadingFind
  )
})

const formatValue = (value: string | string[] | undefined): string => {
  if (!value) return ''
  if (typeof value === 'string') {
    return value.charAt(0).toUpperCase() + value.slice(1)
  }
  return ''
}

const initializeSearchFields = async () => {
  let shouldSearch = false
  if (typeof route.params.disciplineSlug === 'string') {
    mainSearchBarStore.searchService = formatValue(route.params.disciplineSlug)
    shouldSearch = true
  }

  const geolocationStore = useGeolocationStore()
  if (!geolocationStore.storedLocationIsEmpty) {
    mainSearchBarStore.searchLocation = (
      geolocationStore.storedLocation as IStoredGeolocation
    ).city
  }

  if (typeof route.params.city === 'string') {
    mainSearchBarStore.searchLocation = formatValue(route.params.city)
    shouldSearch = true
  }
  if (route.query.lat) {
    mainSearchBarStore.latitude = Number(route.query.lat)
    shouldSearch = true
  }
  if (route.query.lon) {
    mainSearchBarStore.longitude = Number(route.query.lon)
    shouldSearch = true
  }
  if (route.query.address) {
    mainSearchBarStore.searchLocation = formatValue(
      route.query.address as string,
    )
    shouldSearch = true
  }
  return shouldSearch
}

onMounted(async () => {
  const shouldSearch = await initializeSearchFields()
  /**
   * FROM URL
   Si /search go search
   Si /discipline do search discipline et mettre dans le champ discipline mais pas dans l’autre champ mais en utilisant le geocode derrière mais pas dans l’url
   Si /discipline/city do search discipline et mettre dans le champ discipline et city
   Si /discipline/city?lat&lon&address do  searchdiscipline city et lat Lon et address en mettant address dans le champ

   FROM search fields
   Si aucun Field rempli => go to /search et faire une recherche générale avec géolocalisation mais non visible
   Si discipline rempli —> go /discipline et faire le search avec géolocalisationloc auto
   Si discipline. + city fields rempli —> go /discipline/city?lat&lon&addr
   */
  if (shouldSearch) {
    // search()
  }
})
</script>

<style scoped lang="scss">
:deep {
  .v-btn__append {
    margin-left: 0 !important;
  }
}
</style>
